import { useBoolean } from 'usehooks-ts';
import { Pressable, HStack, Text, Popover, ChevronDownIcon, Spacer, VStack } from 'native-base';
import { find, isEmpty, map } from 'lodash';
import { useTranslation } from 'react-i18next';

import { NumberText } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { CurrencyDollarCircle } from '@app/features/flow-chart/icons';
import { useGetBrandSalesVolumeProfiles } from '../hooks';

type SelectSalesVolumeProfilesProps = {
  opsPhaseTitle?: string;
  salesVolumeProfileId?: number;
  salesVolumeProfiles: ReturnType<typeof useGetBrandSalesVolumeProfiles>[0];
  onChange?: (salesVolumeProfileId: number | undefined) => void;
};

export const SelectSalesVolumeProfiles = ({
  opsPhaseTitle,
  salesVolumeProfiles,
  salesVolumeProfileId,
  ...props
}: SelectSalesVolumeProfilesProps) => {
  const { t } = useTranslation();
  const { translate } = useAppLocale();
  const isOpenPopover = useBoolean();

  const profile = find(salesVolumeProfiles.data, ['id', salesVolumeProfileId]);
  const displayText = translate(!isEmpty(profile?.title) ? profile?.title : profile?.category, profile?.titleTranslations);

  const handleChange = (profileId: number) => () => {
    if (salesVolumeProfileId !== profileId && props.onChange) props.onChange(profileId);
    isOpenPopover.setFalse();
  };

  return (
    <Popover
      isKeyboardDismissable
      offset={5}
      isOpen={isOpenPopover.value}
      onClose={isOpenPopover.setFalse}
      placement="bottom right"
      trigger={triggerProps => (
        <Pressable
          {...triggerProps}
          onPress={isOpenPopover.setTrue}
          rounded="lg"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          h={9}
          minW={120}
          borderWidth={1}
          borderColor="gray.300"
          bg="white"
        >
          <HStack space={2} alignItems="center" pr={1}>
            <CurrencyDollarCircle color="primary.600" size={4} />
            <Text fontWeight={500} color="black" fontSize="lg">
              {!isEmpty(displayText) ? displayText : t('flowchart:select_category')}
            </Text>
          </HStack>
          <Spacer />
          <ChevronDownIcon color="gray.700" size="xs" />
        </Pressable>
      )}
    >
      <Popover.Content accessibilityLabel="Sales Volume Profile Selector" shadow="1">
        <Popover.Body p={0} w="auto" minWidth="220px" borderColor="gray.100">
          <VStack px={3} py={2} borderBottomWidth={1} borderBottomColor="gray.200">
            <Text fontWeight={600} color="gray.700" textAlign="center" fontSize="md">
              {`${t('flowchart:select_sales_volume_for')}:`}
            </Text>
            <Text fontWeight={600} color="black" textAlign="center" fontSize="md">
              {opsPhaseTitle ?? 'Phase Not Available'}
            </Text>
          </VStack>
          <VStack>
            {map(salesVolumeProfiles.data, _ => (
              <Pressable key={_.id} onPress={handleChange(_.id ?? 0)}>
                {({ isHovered }) => {
                  const isActive = _.id === salesVolumeProfileId;
                  return (
                    <HStack alignItems="center" py={2} px={4} bg={isHovered ? 'gray.50' : undefined} justifyContent="space-between">
                      <Text
                        fontWeight={isActive ? 700 : 500}
                        color={isActive ? 'black' : 'gray.700'}
                        textTransform="capitalize"
                        fontSize="md"
                      >
                        {translate(!isEmpty(_.title) ? _.title : _.category, _.titleTranslations)}
                      </Text>
                      <NumberText
                        size="sm"
                        fontWeight={isActive ? 700 : 400}
                        color={isActive ? 'black' : 'gray.500'}
                        prefix="$"
                        value={_?.minimumAmount}
                      />
                    </HStack>
                  );
                }}
              </Pressable>
            ))}
          </VStack>
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
};
